import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Swal from 'sweetalert2';

import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear, SCUIDateRanger } from '../../atomic/atoms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { Grid, FormControl, MenuItem, Select, InputLabel, OutlinedInput, Checkbox, ListItemText, Box } from '@mui/material';
import { PostCheckoutQuery } from '../../services/query';
import TimeHelper from '../../utils/time';
import { downloadExcel } from '../../utils/excelFileHelper';

import reportIcon from '../../assets/icons/svg/pageIcons/cashPayment.svg';
import { FRPT } from './financeReport.classes';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export const FinanceReport: FC<any> = () => {
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(moment().subtract(14, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [documentTypes, setDocumentTypes] = useState<number[]>([]);
    const [documentStatuses, setDocumentStatuses] = useState<number[]>([]);

    const { t } = useTranslation();

    const documentTypeOptions = [
        { value: 1, label: t('financeReport.documentType.sales') },
        { value: 2, label: t('financeReport.documentType.return') },
    ];

    const documentStatusOptions = [
        { value: 2, label: t('financeReport.documentStatus.created') },
        { value: 3, label: t('financeReport.documentStatus.canceled') },
    ];

    const selectedCountry = JSON.parse(localStorage.getItem('selectedCountry') || '{}');

    const handleChangeDateRange = (event: any) => {
        setStartDate(moment(event[0], 'DD/MM/YYYY').format('YYYY-MM-DD'));
        setEndDate(moment(event[1], 'DD/MM/YYYY').format('YYYY-MM-DD'));
    };

    const handleDocumentTypeChange = (event: any) => {
        const value = event.target.value;
        setDocumentTypes(typeof value === 'string' ? value.split(',').map(Number) : value);
    };

    const handleDocumentStatusChange = (event: any) => {
        const value = event.target.value;
        setDocumentStatuses(typeof value === 'string' ? value.split(',').map(Number) : value);
    };

    const handleDownloadExcel = async () => {
        if (documentTypes.length === 0) {
            Swal.fire(t('financeReport.error.selectDocumentType'), '', 'error');
            return;
        }

        if (documentStatuses.length === 0) {
            Swal.fire(t('financeReport.error.selectDocumentStatus'), '', 'error');
            return;
        }

        setLoading(true);

        try {
            const params = {
                CountryId: selectedCountry?.countryId || 1,
                StartDate: startDate,
                EndDate: endDate,
                DocumentType: documentTypes,
                DocumentStatus: documentStatuses,
            };

            const response = await PostCheckoutQuery.GetFinanceReport(params);

            if (!response?.isSucceed) {
                Swal.fire(t('general.anErrorOccured'), response?.message || '', 'error');
                return;
            }
            const excelData = response?.data?.financeReports?.map((item: any) => ({
                clientName: item.clientName || '-',
                postalCode: item.postalCode || '-',
                county: item.county || '-',
                orderNumber: item.orderNumber || '-',
                shipmentNumber: item.shipmentNumber || '-',
                invoiceDate: item.invoiceDate ? TimeHelper.formatDateHour(item.invoiceDate) : '-',
                invoiceNumber: item.invoiceNumber || '-',
                invoiceTotalAmount: item.invoiceTotalAmount?.toFixed(2) || '0.00',
                invoiceNetAmount: item.invoiceNetAmount?.toFixed(2) || '0.00',
                invoiceTotalTax: item.invoiceTotalTax?.toFixed(2) || '0.00',
                invoiceStatus: item.invoiceStatus || '-',
            }));

            downloadExcel({
                excelDownloadData: excelData,
                excelDownloadFolderName: t('financeReport.title'),
                hiddenExcelColumn: [],
                fileNameOverride: undefined,
                logo: undefined,
            });

            Swal.fire(t('financeReport.success.downloaded'), '', 'success');
        } catch (error: any) {
            console.error('Error downloading finance report:', error);
            Swal.fire(t('financeReport.error.title'), error?.message || t('financeReport.error.generic'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setStartDate(moment().subtract(14, 'days').format('YYYY-MM-DD'));
        setEndDate(moment().format('YYYY-MM-DD'));
        setDocumentTypes([]);
        setDocumentStatuses([]);
    };

    return (
        <SCUIContent>
            <Box className={FRPT.page}>
            <Grid
                className={FRPT.topBarGrid}
                container
                direction="row"
                spacing={2}
                sx={{ marginLeft: '0.5rem' }}
            >
                <Grid
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <div className={FRPT.breadcrumbWrap}>
                        <SCUIBreadCrumb
                            title={t('financeReport.title')}
                            componentLink={'/financeReport'}
                            style={{ cursor: 'pointer' }}
                            onClick={handleClear}
                        />
                    </div>
                    <div className={FRPT.loadingWrap}>
                        <SCUILoadingLinear display={loading} />
                    </div>
                </Grid>
            </Grid>
            <Grid
                className={FRPT.mainAreaGrid}
                container
                justifyContent="center"
                alignItems="center"
                sx={{ minHeight: '60vh' }}
            >
                <Grid>
                    <div
                        className={FRPT.centerColumn}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignContent: 'center',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <div className={FRPT.noRecordWrap}>
                        <SCUINoRecordComponent
                            text={t('financeReport.description')}
                            height={'140px'}
                            width={'140px'}
                            fontSize="12px"
                            img={reportIcon}
                        />
                        </div>

                        <FormControl
                            className={FRPT.documentTypeField}
                            style={{
                                width: 450,
                                marginTop: '1.5rem',
                                marginBottom: '1rem',
                            }}
                        >
                            <InputLabel id="document-type-label">{t('financeReport.documentType.label')}</InputLabel>
                            <Select
                                className={FRPT.inputSelectDocumentTypes}
                                labelId="document-type-label"
                                id="document-type-select"
                                multiple
                                value={documentTypes}
                                onChange={handleDocumentTypeChange}
                                input={<OutlinedInput label={t('financeReport.documentType.label')} />}
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return '';
                                    }
                                    const labels = selected.map((value) => {
                                        const option = documentTypeOptions.find((opt) => opt.value === value);
                                        return option?.label || value;
                                    });
                                    return labels.join(', ');
                                }}
                                MenuProps={MenuProps}
                            >
                                {documentTypeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Checkbox checked={documentTypes.indexOf(option.value) > -1} />
                                        <ListItemText primary={option.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            className={FRPT.documentStatusField}
                            style={{
                                width: 450,
                                marginBottom: '1rem',
                            }}
                        >
                            <InputLabel id="document-status-label">
                                {t('financeReport.documentStatus.label')}
                            </InputLabel>
                            <Select
                                className={FRPT.inputSelectDocumentStatuses}
                                labelId="document-status-label"
                                id="document-status-select"
                                multiple
                                value={documentStatuses}
                                onChange={handleDocumentStatusChange}
                                input={<OutlinedInput label={t('financeReport.documentStatus.label')} />}
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return '';
                                    }
                                    const labels = selected.map((value) => {
                                        const option = documentStatusOptions.find((opt) => opt.value === value);
                                        return option?.label || value;
                                    });
                                    return labels.join(', ');
                                }}
                                MenuProps={MenuProps}
                            >
                                {documentStatusOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Checkbox checked={documentStatuses.indexOf(option.value) > -1} />
                                        <ListItemText primary={option.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Grid
                            className={FRPT.dateRangeWrap}
                            sx={{ marginBottom: 2, width: 450 }}
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                            }}
                        >
                            <SCUIDateRanger
                                fromDate={moment().subtract(14, 'days').format('DD/MM/YYYY')}
                                toDate={moment().format('DD/MM/YYYY')}
                                setDateRange={handleChangeDateRange}
                                dateFormat="DD/MM/YYYY"
                                fullWidth
                            />
                        </Grid>

                        <FormControl className={FRPT.actionsDownloadWrap} style={{ width: 450, marginBottom: '1rem' }}>
                            <SCUIButton
                                className={FRPT.inputButtonDownloadExcel}
                                text={t('financeReport.downloadExcel')}
                                type="primary"
                                sizeType="default"
                                size={'l'}
                                onClick={handleDownloadExcel}
                                disabled={loading}
                            />
                        </FormControl>

                        <FormControl className={FRPT.actionsClearWrap} style={{ width: 450 }}>
                            <SCUIButton
                                className={FRPT.inputButtonClear}
                                text={t('general.clear')}
                                type="secondary"
                                sizeType="default"
                                size={'l'}
                                onClick={handleClear}
                                disabled={loading}
                            />
                        </FormControl>
                    </div>
                </Grid>
            </Grid>
            </Box>
        </SCUIContent>
    );
};
